import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut } from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ACCOUNT_MENU_ITEMS } from '@/config/navConfig';

export function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleLogout = async () => {
    await logout();
    sessionStorage.removeItem('returnUrl');
    navigate('/', { replace: true });
  };

  if (!user) {
    return (
      <div className="hidden md:flex items-center gap-2">
        <Button variant="outline" asChild>
          <Link to="/login-form">{t('nav.login')}</Link>
        </Button>
        <Button asChild>
          <Link to="/register">{t('nav.register')}</Link>
        </Button>
      </div>
    );
  }

  const resolvedName = (user.role === 'HELPER' ? user.helperName : undefined) ?? user.username;
  const initials = (resolvedName || 'U').slice(0, 2).toUpperCase();
  const displayName = resolvedName || user.email || '';
  const roleLabel =
    user.role === 'EMPLOYER'
      ? t('role.employer', '僱主')
      : user.role === 'ADMIN'
        ? t('role.admin', '管理員')
        : t('role.helper', '外傭');

  const visibleItems = ACCOUNT_MENU_ITEMS.filter(
    item =>
      (!item.employerOnly || user.role === 'EMPLOYER') &&
      (!item.helperOnly || user.role === 'HELPER'),
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="flex items-center gap-2 px-2">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-primary text-primary-foreground text-sm">
              {initials}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium max-w-[120px] truncate">{displayName}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-1.5">
          <p className="text-sm font-medium">{displayName}</p>
          <p className="text-xs text-muted-foreground">{roleLabel}</p>
        </div>
        <DropdownMenuSeparator />

        {visibleItems.map(({ path, icon: Icon, labelKey }) => (
          <DropdownMenuItem key={path} asChild>
            <Link to={path} className="flex items-center gap-2 cursor-pointer w-full">
              <Icon className="h-4 w-4" />
              <span>{t(labelKey)}</span>
            </Link>
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive cursor-pointer">
          <LogOut className="h-4 w-4 mr-2" />
          <span>{t('nav.logout')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
